/**
 * BillingEncountersTableHeader Component
 *
 * Responsive header row for the billing encounters table.
 * Adapts columns based on the layout mode determined by screen width.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

interface BillingEncountersTableHeaderProps {
  layout: TableLayoutMode;
}

export const BillingEncountersTableHeader: React.FC<BillingEncountersTableHeaderProps> = ({
  layout,
}) => {
  const theme = useTheme();

  if (layout === 'card') return null;

  const headerStyle = {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const showProvider = layout === 'full' || layout === 'standard';
  const showService = layout === 'full' || layout === 'standard';
  const showCptCharges = layout === 'full' || layout === 'standard';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: getGridTemplateColumns(layout),
        gap: GRID_GAP,
        alignItems: 'center',
        px: 2,
        py: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      {/* Icon spacer */}
      <Box />

      {/* Date */}
      <Typography variant="caption" sx={headerStyle}>
        Date
      </Typography>

      {/* Provider */}
      {showProvider && (
        <Typography variant="caption" sx={headerStyle}>
          Provider
        </Typography>
      )}

      {/* Patient */}
      <Typography variant="caption" sx={headerStyle}>
        Patient
      </Typography>

      {/* Service Type */}
      {showService && (
        <Typography variant="caption" sx={headerStyle}>
          Service
        </Typography>
      )}

      {/* CPT / Charges */}
      {showCptCharges && (
        <Typography variant="caption" sx={headerStyle}>
          CPT / Charges
        </Typography>
      )}

      {/* Note Status */}
      <Typography variant="caption" sx={headerStyle}>
        Note
      </Typography>

      {/* Claim Status */}
      <Typography variant="caption" sx={headerStyle}>
        Claim
      </Typography>

      {/* Ready to Bill */}
      <Typography variant="caption" sx={headerStyle}>
        Ready
      </Typography>

      {/* Actions spacer */}
      <Box />
    </Box>
  );
};
